import type { CostTrendPoint } from '../types'

/** `part` as a % of `whole` with one decimal; null unless `whole` is positive. Both are API decimal strings. */
export function shareOf(part: string, whole: string): number | null {
  const w = Number(whole)
  if (!(w > 0)) return null
  return Math.round((Number(part) / w) * 1000) / 10
}

export const formatPercent = (n: number, locale: string): string => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n)

export const TREND_KEYS = ['revisedBudget', 'committed', 'actual', 'projectedFinalCost'] as const
export type TrendKey = (typeof TREND_KEYS)[number]

/** A line needs two days (spec §4); until then the chart explains that it fills nightly. */
export const hasTrend = (points: CostTrendPoint[]): boolean => points.length >= 2

/** One label per day and one numeric series per value, in TREND_KEYS order. */
export function trendSeries(points: CostTrendPoint[]): { labels: string[]; series: { key: TrendKey; data: number[] }[] } {
  return { labels: points.map((p) => p.date), series: TREND_KEYS.map((key) => ({ key, data: points.map((p) => Number(p[key])) })) }
}

export type HistoryFilter = 'all' | 'budget' | 'contracts' | 'changes' | 'applications' | 'invoices'
export const HISTORY_FILTERS: HistoryFilter[] = ['all', 'budget', 'contracts', 'changes', 'applications', 'invoices']
/** Spec §5: the project history filter → the entity types it asks for; undefined = every type. */
export const HISTORY_FILTER_TYPES: Record<HistoryFilter, string[] | undefined> = {
  all: undefined,
  budget: ['NODE', 'IMPORT', 'SETTINGS'],
  contracts: ['CONTRACT'],
  changes: ['CHANGE'],
  applications: ['APPLICATION'],
  invoices: ['INVOICE', 'EXPENSE'],
}

/** The alert threshold field (spec §1): empty = off, otherwise a whole number 0–1000. */
export function parseThreshold(text: string): { ok: true; value: number | null } | { ok: false } {
  const s = text.trim()
  if (s === '') return { ok: true, value: null }
  if (!/^\d+$/.test(s)) return { ok: false }
  const n = Number(s)
  return n <= 1000 ? { ok: true, value: n } : { ok: false }
}

/** `YYYY-MM` → short month and year in `locale`; built from numbers so no timezone can shift the month. */
export function monthLabel(month: string, locale: string): string {
  const [y, m] = month.split('-').map(Number)
  return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(new Date(y, m - 1, 1))
}
